// Continuity strip shown on any post-submit screen when the student is inside
// an assignment run. Reads `?assignment=<aid>` from the URL, fetches the
// bundle, and offers a one-click jump to the next incomplete item — or a
// "Assignment complete" state if every item is done. Fetches on mount only;
// the parent screen already re-renders after submission, which is when we
// want the "next" pointer to move forward.
import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight, CheckCircle2 } from 'lucide-react';

import { api } from '@/lib/api';

function withAssignment(path, aid) {
  if (!path || !aid) return path;
  return path + (path.includes('?') ? '&' : '?') + `assignment=${aid}`;
}

export default function AssignmentNextCTA() {
  const { search } = useLocation();
  const aid = new URLSearchParams(search).get('assignment');
  const [bundle, setBundle] = useState(null);

  useEffect(() => {
    if (!aid) return;
    let dead = false;
    (async () => {
      try {
        const r = await api.myAssignment(aid);
        if (!dead) setBundle(r.data.assignment);
      } catch { /* bundle got deleted or never existed — quietly hide the strip */ }
    })();
    return () => { dead = true; };
  }, [aid]);

  if (!aid || !bundle) return null;
  const nextItem = bundle.items.find((it) => !it.completed);
  const done = bundle.completed_count;
  const total = bundle.total_count;

  return (
    <div className="mt-6 border border-indigo-200 bg-indigo-50/50 px-5 py-4" data-testid="assignment-next-cta">
      <div className="flex items-center justify-between gap-4">
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wide text-indigo-700">In assignment</p>
          <p className="mt-0.5 truncate font-display text-[15px] font-semibold text-slate-900">{bundle.title}</p>
          <p className="mt-0.5 text-xs text-slate-500">{done} / {total} done · assigned by {bundle.assigned_by_name}</p>
        </div>
        {nextItem ? (
          <Link
            to={withAssignment(nextItem.cta_path, aid)}
            data-testid="assignment-next-item"
            className="inline-flex shrink-0 items-center gap-1 rounded bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
          >
            Next item <ArrowRight className="h-4 w-4" />
          </Link>
        ) : (
          <div className="flex shrink-0 items-center gap-3" data-testid="assignment-complete-strip">
            <span className="inline-flex items-center gap-1 text-sm font-medium text-emerald-700">
              <CheckCircle2 className="h-4 w-4" /> Complete
            </span>
            <Link
              to={`/assignments/${aid}`}
              className="inline-flex items-center gap-1 rounded border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Return
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
